import styled from "@emotion/styled";
import { tokens } from "../../design-system/tokens";

export const TableViewport = styled.div<{ height?: number | string }>`
  width: 100%;
  margin-bottom: 100px;
  padding: 16px 0;
`;

export const TableHorizontalScroll = styled.div`
  width: 100%;
  overflow-x: auto; 
`;

export const TableScrollContainer = styled.div`
  /* Altura exacta para header + 5 filas de datos */
  max-height: calc(${tokens.spacing["2xl"]} +
    5 * ${tokens.spacing["2xl"]} +
    5 * 2px
  );
  min-height: 200px;
  overflow-y: auto;
  overflow-x: visible;
  position: relative;

  /* Indicador sutil de que hay más contenido abajo */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 8px;
    background: linear-gradient(transparent, rgba(0, 0, 0, 0.1));
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  /* Mostrar indicador solo cuando hay scroll disponible Y hay datos */
  &[data-has-scroll="true"][data-has-data="true"]::after {
    opacity: 1;
  }

  /* Asegurar que no se muestre cuando no hay datos */
  &[data-has-data="false"]::after {
    opacity: 0 !important;
  }

  /* Estilos del scrollbar */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.65) rgba(255, 255, 255, 0.12);

  &::-webkit-scrollbar {
    width: 10px;
    background: rgba(255, 255, 255, 0.05);
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    margin: 8px 2px;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08),
      inset 0 0 6px rgba(0, 0, 0, 0.25);
  }

  &::-webkit-scrollbar-thumb {
    background: ${tokens.colors.blue[900]};
    border-radius: 12px;
    background-clip: padding-box;
    border: 3px solid transparent;

    &:hover {
      background: ${tokens.colors.blue[500]};
    }

    &:active {
      background: ${tokens.colors.blue[400]};
    }
  }
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 2px;
  color: ${tokens.colors.basics[0]};
  table-layout: fixed;
  border-radius: ${tokens.borderRadius.lg};

  th,
  td {
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  th {
    overflow: visible;
  }

  td {
    overflow: hidden;
  }

  /* Cuando hay datos, usar max-content para el ancho */
  &[data-has-data="true"] {
    width: max-content;
    min-width: 100%;
  }
`;

export const TableHeader = styled.thead``;

export const HeaderRow = styled.tr`
  background: ${tokens.colors.blue[900]};
  height: ${tokens.spacing["2xl"]};
`;

export const HeaderCell = styled.th`
  position: sticky;
  top: 0;
  z-index: 2;
  font-weight: ${tokens.typography.fontWeight.semibold};
  font-size: ${tokens.typography.fontSize.sm};
  text-align: left;
  padding: ${tokens.spacing.sm} ${tokens.spacing.md};
  font-family: ${tokens.typography.fontFamily.primary};
  background: ${tokens.colors.blue[900]};
`;

export const TableBody = styled.tbody`
  border-radius: 0 0 12px 12px;
`;

export const TableRow = styled.tr`
  border-bottom: 2px solid transparent;
  height: ${tokens.spacing["2xl"]};
  transition: all 200ms ease;
  & + & td {
    border-top: 2px solid transparent;
  }

  &[data-clickable="true"] {
    cursor: pointer;

    &:hover td {
      background: ${tokens.colors.transparencies[50]};
    }

    &:focus {
      outline: 2px solid ${tokens.colors.secondary[500]};
      outline-offset: -2px;
    }
  }

  &[data-clickable="true"]:active td {
    font-size: calc(${tokens.typography.fontSize.sm} * 1.15);
    font-weight: ${tokens.typography.fontWeight.semibold};
  }
`;

export const TableCell = styled.td`
  font-size: ${tokens.typography.fontSize.sm};
  padding: ${tokens.spacing.sm} ${tokens.spacing.md};
  vertical-align: middle;
  background-color: rgba(255, 255, 255, 0.1);
  background-clip: padding-box;
  border-bottom: 2px solid transparent;
  font-family: ${tokens.typography.fontFamily.primary};
  transition: font-size 200ms ease, font-weight 200ms ease;

  tr:last-child & {
    border-bottom: none;
  }
`;

export const FullRowCell = styled.td`
  text-align: center;
  color: ${tokens.colors.basics[1000]};
  font-family: ${tokens.typography.fontFamily.primary};
  width: 100%;
  background-color: transparent !important;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal !important;
  overflow-wrap: break-word;

  /* Asegurar que el contenido se centre correctamente */
  & > * {
    margin: 0 auto;
    max-width: 100%;
    word-wrap: break-word;
    word-break: break-word;
    white-space: normal !important;
    overflow-wrap: break-word;
  }
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${tokens.spacing.sm};
  color: ${tokens.colors.basics[1100]};
`;

export const ResultsCount = styled.div`
  font-size: ${tokens.typography.fontSize.sm};
  font-family: ${tokens.typography.fontFamily.primary};
  color: ${tokens.colors.basics[0]};
`;
